/**
 * Dynamic import with retry and cache-busting for handling deployment updates
 * 
 * When a new version is deployed, old chunk files are removed. Users with the old
 * version loaded will get "Failed to fetch dynamically imported module" errors.
 * 
 * Chrome caches these failures, so we need cache-busting (adding timestamps) to force
 * a fresh import attempt.
 * 
 * Based on: https://stackoverflow.com/a/76200536
 */

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

/**
 * Extract module path from error message or import function
 */
function getModulePath(importFn, error) {
  // Try to extract URL from error message
  if (error && error.message) {
    const match = error.message.match(/https?:\/\/[^\s)]+/);
    if (match) {
      return match[0];
    }
  }
  
  // Fallback: try to get the path from the import function
  // This is tricky because the import path is inside the function
  const fnString = importFn.toString();
  const pathMatch = fnString.match(/import\s*\(\s*['"`]([^'"`]+)['"`]/);
  if (pathMatch) {
    return pathMatch[1];
  }
  
  return null;
}

/**
 * Retry dynamic import with cache busting
 * 
 * @param {Function} importFn - Function that returns import() promise
 * @returns {Promise} The imported module
 * 
 * @example
 * const MyComponent = () => dynamicImportWithRetry(() => import('./MyComponent.vue'))
 */
export async function dynamicImportWithRetry(importFn) {
  console.log('[Dynamic Import] Attempting import...');
  try {
    const result = await importFn();
    console.log('[Dynamic Import] Import succeeded');
    return result;
  } catch (error) {
    console.log('[Dynamic Import] Import failed:', error.message);
    
    // Check if it's a chunk load error
    const isChunkError = 
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Failed to load module script') ||
      error.message?.includes('error loading dynamically imported module') ||
      error.message?.includes('Importing a module script failed');
    
    if (!isChunkError) {
      console.log('[Dynamic Import] Not a chunk error, re-throwing');
      throw error;
    }
    
    console.log('[Dynamic Import] Chunk load error detected, starting retry logic');
    
    // Try to get the module path
    let modulePath = getModulePath(importFn, error);
    console.log('[Dynamic Import] Module path:', modulePath);
    
    // Retry with exponential backoff and cache busting
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      // Wait with exponential backoff
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * Math.pow(2, attempt - 1)));
      
      try {
        // If we have a URL, use cache busting
        if (modulePath) {
          try {
            const url = new URL(modulePath, window.location.origin);
            url.searchParams.set('t', Date.now().toString());
            url.searchParams.set('retry', attempt.toString());
            
            const module = await import(/* @vite-ignore */ url.href);
            console.log(`[Dynamic Import] Cache-busting succeeded on attempt ${attempt}`);
            return module;
          } catch (urlError) {
            // Continue to next attempt
          }
        }
        
        // Fallback: try the original import again
        const module = await importFn();
        console.log(`[Dynamic Import] Retry ${attempt} succeeded`);
        return module;
        
      } catch (retryError) {
        if (attempt === MAX_RETRIES) {
          console.error('[Dynamic Import] All retry attempts exhausted, forcing page reload');
          
          // Final resort: force a full page reload with cache busting
          const reloadUrl = new URL(window.location.href);
          reloadUrl.searchParams.set('reload', Date.now().toString());
          window.location.href = reloadUrl.href;
          
          // Throw to prevent further execution while page reloads
          throw new Error('Forcing page reload due to persistent chunk load failure');
        }
      }
    }
    
    // Should never reach here
    throw error;
  }
}

/**
 * Wrapper for Vue Router lazy loading with retry
 * 
 * @example
 * const Home = lazyLoad(() => import('./views/Home.vue'))
 */
export const lazyLoad = (importFn) => {
  return () => dynamicImportWithRetry(importFn);
};

export default dynamicImportWithRetry;
